/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { Order, PagingRequest, QueryResult, ResourceQueryResult, Services } from '../util/services';
import { EmptyMessage } from '../util/messages';
import { List, Map, Set } from 'immutable';
import { OffsetDateTime } from '../util/dates';
import { MessageTemplateResource, MessageTemplateResourceService } from './message-template-resource.service';
/* eslint-enable */

@Injectable()
export class MessageTemplateService implements MessageTemplate.Service {

  constructor(private resourceService: MessageTemplateResourceService) {
  }

  query(request: MessageTemplate.QueryRequest): Observable<QueryResult<MessageTemplate.MessageTemplate>> {
    return Observable.create((observer: Observer<QueryResult<MessageTemplate.MessageTemplate>>) => {
      const resourceRequest: MessageTemplateResource.QueryRequest = {
        id: Services.createIdParameter(request.id),
        external_id: request.externalId,
        name: request.name,
        title: request.title,
        disabled: request.disabled,
        order: Services.createOrderFieldParameter(Keys.toOrderFieldKey, request.orders),
        page_number: request.paging ? request.paging.pageNumber : undefined,
        number_of_items: request.paging ? request.paging.numberOfItems : undefined,
        no_progress_bar: request.noProgressBar
      };
      return this.resourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<MessageTemplateResource.MessageTemplate>) => {
          observer.next({
            items: this.toPublicList(result.items),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  get(request: MessageTemplate.GetRequest): Observable<MessageTemplate.MessageTemplate> {
    return Observable.create((observer: Observer<MessageTemplate.MessageTemplate>) => {
      const resourceRequest: MessageTemplateResource.GetRequest = {
        id: request.id
      };
      return this.resourceService.get(resourceRequest).subscribe(
        (result: MessageTemplateResource.MessageTemplate) => {
          observer.next(this.toPublic(result));
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  create(request: MessageTemplate.CreateRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: MessageTemplateResource.CreateRequest = {
        external_id: request.externalId,
        name: request.name,
        title: request.title,
        template: request.template,
      };
      return this.resourceService.create(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  update(request: MessageTemplate.UpdateRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: MessageTemplateResource.UpdateRequest = {
        id: request.id,
        external_id: request.externalId,
        name: request.name,
        title: request.title,
        template: request.template,
      };
      return this.resourceService.update(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  setDisabled(request: MessageTemplate.DisableRequest): Observable<EmptyMessage> {
    return Observable.create((observer: Observer<EmptyMessage>) => {
      const resourceRequest: MessageTemplateResource.DisableRequest = {
        id: request.id,
        disabled: request.disabled
      };
      return this.resourceService.disable(resourceRequest).subscribe(
        (result: EmptyMessage) => {
          observer.next(result);
        },
        (error: Error) => {
          observer.error(error);
        },
        () => {
          observer.complete();
        });
    });
  }

  private toPublicList(resourceList: MessageTemplateResource.MessageTemplate[]): List<MessageTemplate.MessageTemplate> {
    return List.of(...resourceList.map((r) => this.toPublic(r)));
  }

  private toPublic(r: MessageTemplateResource.MessageTemplate): MessageTemplate.MessageTemplate {
    return {
      id: r.id,
      externalId: r.external_id,
      name: r.name,
      title: r.title,
      template: r.template,
      creationTime: Services.toOffsetDateTime(r.creation_time),
      updateTime: Services.toOffsetDateTime(r.update_time),
      disabled: r.disabled
    };
  }

}


export namespace MessageTemplate {

  export interface Service {

    // <editor-fold desc="CRUD">

    query(request: QueryRequest): Observable<QueryResult<MessageTemplate>>;

    get(request: GetRequest): Observable<EmptyMessage>;

    create(request: CreateRequest): Observable<EmptyMessage>;

    update(request: UpdateRequest): Observable<EmptyMessage>;

    setDisabled(request: DisableRequest): Observable<EmptyMessage>;

    // </editor-fold>

  }

  export interface MessageTemplate {
    id: number;
    externalId: string;
    name: string;
    title: string;
    template: string;
    disabled: boolean;
    creationTime: OffsetDateTime;
    updateTime: OffsetDateTime;
  }

  export interface QueryRequest {
    id?: Set<number>;
    externalId?: string;
    name?: string;
    title?: string;
    orders?: Set<Order<OrderField>>;
    paging?: PagingRequest;
    disabled?: boolean;
    noProgressBar?: boolean;
  }

  export interface GetRequest {
    id: number;
  }

  export interface CreateRequest {
    externalId?: string;
    name: string;
    title: string;
    template: string;
  }

  export interface UpdateRequest {
    id: number;
    externalId: string;
    name: string;
    title: string;
    template: string;
  }

  export interface DisableRequest {
    id: number;
    disabled: boolean;
  }

  export enum OrderField {
    ID,
    EXTERNAL_ID,
    NAME,
    CREATION_TIME,
    UPDATE_TIME
  }

  export enum ValidatedField {
    UNKNOWN,

    EXTERNAL_ID,
    NAME,
    TITLE
  }

}

class Keys {

  private static readonly ID = 'id';
  private static readonly EXTERNAL_ID = 'external_id';
  private static readonly NAME = 'name';
  private static readonly TITLE = 'title';
  private static readonly CREATION_TIME = 'creation_time';
  private static readonly UPDATE_TIME = 'update_time';

  private static readonly orderFieldKeyMap: Map<MessageTemplate.OrderField, string> = Map.of(
    MessageTemplate.OrderField.ID, Keys.ID,
    MessageTemplate.OrderField.EXTERNAL_ID, Keys.EXTERNAL_ID,
    MessageTemplate.OrderField.NAME, Keys.NAME,
    MessageTemplate.OrderField.CREATION_TIME, Keys.CREATION_TIME,
    MessageTemplate.OrderField.UPDATE_TIME, Keys.UPDATE_TIME,
  );

  private static readonly keyValidatedFieldMap: Map<string, MessageTemplate.ValidatedField> = Map.of(
    MessageTemplate.ValidatedField.EXTERNAL_ID, Keys.EXTERNAL_ID,
    MessageTemplate.ValidatedField.NAME, Keys.NAME,
    MessageTemplate.ValidatedField.TITLE, Keys.TITLE
  );

  public static toOrderFieldKey(field: MessageTemplate.OrderField): string {
    return Keys.orderFieldKeyMap.get(field)!;
  }

  public static toValidatedField(fieldKey: string): MessageTemplate.ValidatedField {
    return Keys.keyValidatedFieldMap.get(fieldKey, MessageTemplate.ValidatedField.UNKNOWN);
  }

}
